import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useRouter } from "next/router";
import { FC, useCallback, MouseEvent } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { EVENT_NAMES, trackEvent } from "lib/tracking/segment";
import { Cta15Content } from "types/graphql/generated";

interface Props {
  content: Cta15Content;
}

const Cta15: FC<Props> = withErrorBoundary(({ content }) => {
  const { asPath } = useRouter();

  const onClickCta = useCallback((event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;

    // only track on pricing page for RND401K-7420
    if (asPath !== "/pricing") {
      return null;
    }

    const eventName = EVENT_NAMES.buttonClicked as keyof any;

    // Follow tracking standardization, see:
    // https://guideline.atlassian.net/wiki/spaces/ENG/pages/269943217/Segment+Event+Types#Button-Clicked
    const data = {
      details: {
        [asPath]: "Mid-Page Get Started CTA",
      },
      text: target.innerText,
    };

    trackEvent(eventName, data);
  }, []);

  return (
    <StyledCta15 className="Content">
      <div className="Cta15__innerContainer">
        <Markdown className="Font_24_32_600">{content.cta15Title}</Markdown>
        <PipelineRoutingLinkButton className="Cta15__button" href={content.cta15ButtonLink} onClick={onClickCta}>
          {content.cta15ButtonLabel}
        </PipelineRoutingLinkButton>
      </div>
    </StyledCta15>
  );
}, {});

const StyledCta15 = styled.section`
  .Cta15__innerContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 32px 40px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["30"]};
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.fog["10"]};
  }

  .Cta15__button {
    margin-left: 32px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Cta15__innerContainer {
      flex-direction: column;
      align-items: flex-start;
      padding: 32px;
    }

    .Cta15__button {
      margin-top: 24px;
      margin-left: 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Cta15__innerContainer {
      padding: 24px;
    }
  }
`;

gql`
  fragment Cta15Content on ComponentCtaCta15 {
    cta15Title
    cta15ButtonLink
    cta15ButtonLabel
  }
`;

export { Cta15 };
export type { Props as Cta15Props };
